import { FC, useMemo, useState } from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useResponsive } from 'src/hooks/useResponsiveHook';
import { useDashboardFinanceHook } from '../hooks/useDashboardFinanceHook';
import {
  ActionItems,
  SummaryCards,
  UpcomingItems,
  TimeRangePicker,
  SummaryCardData,
} from './shared';
import {
  IFinanceActionItem,
  IFinanceUpcomingItem,
  ITimeRangeParams,
} from '../../../../apis/dashboard/dashboard.interfaces';
import {
  AttachMoney,
  Receipt,
  Warning,
  TrendingUp,
} from '@mui/icons-material';
import { getDashboardNavigationPaths } from '../../../../apis/dashboard/dashboard.helpers';
import { useNavigate } from 'react-router-dom';

const DashboardFinanceTabView: FC = () => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [timeRange, setTimeRange] = useState<ITimeRangeParams>({
    period: 'monthly',
  });

  // Use hook directly with time range params - React Query will refetch automatically
  const { data: financeData, isLoading } = useDashboardFinanceHook(timeRange);

  const navigationPaths = getDashboardNavigationPaths();
  const navigate = useNavigate();

  // Prepare action items
  const actionItems = useMemo<IFinanceActionItem[]>(() => {
    if (!financeData?.actionItems) {
      return [];
    }
    return [
      ...financeData.actionItems.overdue.items,
      ...financeData.actionItems.partialPayments.items,
      ...financeData.actionItems.paymentIssues.items,
    ];
  }, [financeData]);

  // Prepare upcoming items
  const upcomingItems = useMemo<IFinanceUpcomingItem[]>(() => {
    if (!financeData?.upcomingItems) {
      return [];
    }
    return [
      ...financeData.upcomingItems.feeDeadlines,
      ...financeData.upcomingItems.scholarshipDisbursements,
      ...financeData.upcomingItems.grantDeadlines,
      ...financeData.upcomingItems.reportDeadlines,
    ];
  }, [financeData]);

  // Prepare summary cards
  const summaryCards = useMemo<SummaryCardData[]>(() => {
    const cards: SummaryCardData[] = [];

    if (financeData?.summary) {
      cards.push({
        id: 'total-collected',
        title: translateText('dashboard.finance.summary.totalCollected'),
        value: new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 0,
        }).format(financeData.summary.totalCollected || 0),
        icon: <AttachMoney />,
        color: 'success',
        navigationPath: navigationPaths.finance.reports,
        isCritical: true, // Always show on mobile
      });

      cards.push({
        id: 'total-due',
        title: translateText('dashboard.finance.summary.totalDue'),
        value: new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          maximumFractionDigits: 0,
        }).format(financeData.summary.totalDue || 0),
        icon: <Warning />,
        color: 'error',
        navigationPath: navigationPaths.finance.list,
        isCritical: true, // Always show on mobile
      });

      cards.push({
        id: 'collection-rate',
        title: translateText('dashboard.finance.summary.collectionRate'),
        value: financeData.summary.collectionRate
          ? `${financeData.summary.collectionRate}%`
          : '0%',
        icon: <TrendingUp />,
        color: 'primary',
        navigationPath: navigationPaths.finance.reports,
      });

      cards.push({
        id: 'total-transactions',
        title: translateText('dashboard.finance.summary.totalTransactions'),
        value: financeData.summary.transactions?.count || 0,
        icon: <Receipt />,
        color: 'info',
        navigationPath: navigationPaths.finance.reports,
      });
    }

    return cards;
  }, [financeData, translateText, navigationPaths]);

  const handleTimeRangeChange = (newRange: ITimeRangeParams) => {
    setTimeRange(newRange);
    // React Query will automatically refetch when timeRange changes (query key includes params)
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        data-testid="dashboard-finance-tab-loading"
      >
        <Typography variant="body2" color="textSecondary">
          {translateText('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={4}
      sx={{
        pb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
      data-testid="dashboard-finance-tab-container"
    >

      {/* Summary Section - Past (with time range picker) */}
      {summaryCards.length > 0 && (
        <Grid item xs={12} mb={4}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h6" fontWeight={600}>
              {translateText('dashboard.finance.summary.title')}
            </Typography>
            <TimeRangePicker
              value={timeRange}
              onChange={handleTimeRangeChange}
              dataTestId="dashboard-finance-time-range"
              showCustomRange={true}
            />
          </Box>
          <SummaryCards
            cards={summaryCards}
            columns={{ xs: 12, sm: 6, md: 3 }}
            dataTestId="dashboard-finance-summary-cards"
            maxMobileCards={2}
          />
        </Grid>
      )}
      {/* Action Items Section - Present/TODOs */}
      {actionItems.length > 0 && (
        <Grid item xs={12} lg={4} mb={3}>
          <ActionItems
            title={translateText('dashboard.finance.actionItems.title')}
            items={actionItems}
            isLoading={isLoading}
            emptyMessage={translateText('dashboard.finance.actionItems.empty')}
            dataTestId="dashboard-finance-action-items"
          />
        </Grid>
      )}

      {/* Upcoming Items Section - Future */}
      {upcomingItems.length > 0 && (
        <Grid item xs={12} lg={4} mt={3}>
          <UpcomingItems
            title={translateText('dashboard.finance.upcomingItems.title')}
            items={upcomingItems}
            isLoading={isLoading}
            emptyMessage={translateText('dashboard.finance.upcomingItems.empty')}
            dataTestId="dashboard-finance-upcoming-items"
            maxItems={5}
          />
        </Grid>
      )}

      {/* Recent Activity Section - Collapsible (Hidden on mobile) */}
      {financeData?.recentActivity && financeData.recentActivity.length > 0 && !isMobile && (
        <Grid item xs={12} lg={4} mt={3}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            {translateText('dashboard.finance.recentActivity.title')}
          </Typography>
          <Box
            sx={{
              maxHeight: 500,
              overflowY: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
            }}
          >
            {financeData.recentActivity.map((activity) => (
              <Box
                key={activity.id}
                onClick={() => {
                  if (activity.navigationPath) {
                    navigate(activity.navigationPath);
                  }
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1,
                  px: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  cursor: activity.navigationPath ? 'pointer' : 'default',
                  '&:hover': activity.navigationPath
                    ? {
                      backgroundColor: theme.palette.action.hover,
                      borderRadius: 1,
                    }
                    : {},
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
                data-testid={`dashboard-finance-recent-activity-${activity.id}`}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{activity.description}</Typography>
                  {activity.amount && (
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                        maximumFractionDigits: 0,
                      }).format(activity.amount)}
                    </Typography>
                  )}
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ ml: 2 }}>
                  {new Date(activity.timestamp).toLocaleDateString()}
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default DashboardFinanceTabView;
